import { useCallback, useMemo, useState } from 'react';
import type { SortBy } from 'tea-component/lib/table/addons';
import { clearCache, useUpdateEffect } from 'ahooks';
import type { PageInfoType } from '@op/types/common';
import { useRequestController } from '../../Home/hooks/useRequestController.ts';
import { SKILL_VERSIONS_CACHE_KEY_PREFIX } from '../constants/skillDetail';
import type {
  IGetSkillVersionsListParams,
  IGetSkillVersionsResponse,
} from '../types/skillDetail';

const DEFAULT_PAGE_INFO: PageInfoType = { page: 1, page_size: 20 };

const DEFAULT_SORT: SortBy<string> = {
  by: 'update_time',
  order: 'desc',
};

/**
 * useSkillVersionsData 配置项
 */
export interface IUseSkillVersionsDataOptions {
  /** Skill 唯一标识 */
  skillId: string;
  /** 是否发起请求 */
  enabled: boolean;
}

/**
 * 获取 Skill 版本历史列表（服务端分页 + 排序）
 */
export const useSkillVersionsData = ({
  skillId,
  enabled,
}: IUseSkillVersionsDataOptions) => {
  const [pageInfo, setPageInfo] = useState<PageInfoType>(DEFAULT_PAGE_INFO);
  const [sortInfo, setSortInfo] = useState<SortBy<string>>(DEFAULT_SORT);

  const query = useMemo<IGetSkillVersionsListParams>(
    () => ({
      skill_id: skillId,
      page: pageInfo.page,
      page_size: pageInfo.page_size,
      sort_item: sortInfo.by,
      sort: sortInfo.order || 'desc',
    }),
    [skillId, pageInfo, sortInfo],
  );

  const { data, loading, error, refresh } = useRequestController({
    method: 'GET',
    url: '/api/v1/util/skills/versions/list',
    cacheKey: skillId
      ? `${SKILL_VERSIONS_CACHE_KEY_PREFIX}${skillId}`
      : undefined,
    refreshDeps: query,
    ready: enabled && Boolean(skillId),
    cacheTime: 1800000,
    staleTime: 1800000,
  });

  const response = data as IGetSkillVersionsResponse | undefined;
  const versions = useMemo(() => response?.list ?? [], [response?.list]);
  const total = response?.total ?? 0;

  useUpdateEffect(() => {
    setPageInfo(DEFAULT_PAGE_INFO);
    setSortInfo(DEFAULT_SORT);
  }, [skillId]);

  const handleSortChange = useCallback((sort: SortBy<string>) => {
    if (skillId) {
      clearCache(`${SKILL_VERSIONS_CACHE_KEY_PREFIX}${skillId}`);
    }
    setPageInfo(DEFAULT_PAGE_INFO);
    setSortInfo({ by: sort.by, order: sort.order || 'desc' });
  }, [skillId]);

  // 分页变化时清除缓存，避免 staleTime 内命中缓存导致不触发请求
  const handlePageChange = useCallback((next: PageInfoType) => {
    if (skillId) {
      clearCache(`${SKILL_VERSIONS_CACHE_KEY_PREFIX}${skillId}`);
    }
    setPageInfo(next);
  }, [skillId]);

  return {
    versions,
    total,
    loading,
    error,
    pageInfo,
    setPageInfo: handlePageChange,
    sortInfo,
    handleSortChange,
    refreshVersions: refresh,
  };
};
